// Retention Dashboard -- customer-grain retention metrics.
//
// Reads from /api/retention/{kpis,distribution,at-risk,trend}.
// Companion to the CHI Dashboard which lives at site grain.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Legend,
} from 'recharts';

const HEALTHY = '#059669';
const WATCH   = '#d97706';
const RISK    = '#dc2626';

function fmtCurrency(n) {
  if (n === null || n === undefined) return '—';
  return '$' + Math.round(Number(n)).toLocaleString('en-CA');
}
function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toFixed(1) + '%';
}
function fmtMonth(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });
}

function KpiCard({ label, value, sub, valueColor, title }) {
  return (
    <div title={title} style={{
      background: 'var(--color-surface, #fff)',
      border: '1px solid var(--color-border, #e5e7eb)',
      borderRadius: 8,
      padding: 16,
      flex: 1,
      minWidth: 200,
    }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: valueColor || 'inherit' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function Retention() {
  const [kpis, setKpis] = useState(null);
  const [dist, setDist] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get('/api/retention/kpis'),
      axios.get('/api/retention/distribution'),
      axios.get('/api/retention/at-risk'),
      axios.get('/api/retention/trend?months=12'),
    ]).then(([k, d, r, t]) => {
      setKpis(k.data);
      setDist(d.data);
      setAtRisk(r.data.accounts || []);
      setTrend(t.data.months || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <section className="page"><h2>Retention</h2><p>Loading…</p></section>;
  if (!kpis) return <section className="page"><h2>Retention</h2><p>Failed to load.</p></section>;

  // Concentration risk callout: any customer > 10% of book
  const totalDist = dist ? (dist.healthy + dist.watch + dist.risk + dist.grey) : 0;
  const distSum = dist ? Math.max(1, dist.healthy + dist.watch + dist.risk) : 1;

  return (
    <section className="page">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Retention & Churn</h2>
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
          Trailing 12 months · {kpis.customer_count} customers · Total book <strong>{fmtCurrency(kpis.current_arr)}</strong>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <KpiCard
          label="Net revenue retention"
          value={kpis.has_sufficient_history ? fmtPct(kpis.nrr_pct) : '—'}
          sub={kpis.has_sufficient_history ? 'trailing 12mo' : `available after 30 days · ${kpis.snapshot_days}d recorded`}
          valueColor={kpis.nrr_pct >= 100 ? HEALTHY : kpis.nrr_pct !== null ? RISK : undefined}
          title="NRR = (baseline + expansion − contraction − churn) / baseline"
        />
        <KpiCard
          label="Gross revenue churn"
          value={kpis.has_sufficient_history ? fmtPct(kpis.gross_churn_pct) : '—'}
          sub={kpis.has_sufficient_history ? 'trailing 12mo' : 'available after 30 days'}
          valueColor={kpis.gross_churn_pct !== null ? RISK : undefined}
          title="Gross churn = churned_arr / baseline (before expansion)"
        />
        <KpiCard
          label="Logo churn"
          value={kpis.logo_churn_count}
          sub={`customers · ${fmtPct(kpis.logo_churn_pct)}`}
          title="Customers whose sites all went Inactive in the last 12 months"
        />
        <KpiCard
          label="At-risk ARR"
          value={fmtCurrency(kpis.at_risk_arr)}
          sub={`${kpis.at_risk_count} accounts · renews <90d`}
          valueColor={RISK}
          title="Sum of ARR for customers with CHI < 50 AND nearest renewal within 90 days"
        />
      </div>

      {/* Trend chart */}
      <div style={{
        background: 'var(--color-surface, #fff)',
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
      }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Book value trend</h3>
        <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 8 }}>
          {trend.length < 2
            ? 'Building history — trend line lights up as daily snapshots accumulate.'
            : `${trend.length} months of data`}
        </div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => '$' + Math.round(v/1000) + 'k'} />
              <Tooltip
                formatter={(v, n) => [fmtCurrency(v), n === 'arr' ? 'Book ARR' : 'Churned ARR']}
                labelFormatter={fmtMonth}
              />
              <Legend formatter={(v) => v === 'arr' ? 'Book ARR' : 'Churned ARR'} />
              <Line type="monotone" dataKey="arr"     stroke={HEALTHY} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="churned" stroke={RISK} strokeDasharray="4 4" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Distribution bar */}
      {dist && (
        <div style={{
          background: 'var(--color-surface, #fff)',
          border: '1px solid var(--color-border, #e5e7eb)',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Account health distribution</h3>
            <div style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
              Banded by CHI · rolled up from worst-site SHI
            </div>
          </div>
          <div style={{ display: 'flex', height: 32, borderRadius: 4, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
            {dist.healthy > 0 && (
              <div style={{ flex: dist.healthy, background: '#d1fae5', color: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                Healthy {dist.healthy}
              </div>
            )}
            {dist.watch > 0 && (
              <div style={{ flex: dist.watch, background: '#fef3c7', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                Watch {dist.watch}
              </div>
            )}
            {dist.risk > 0 && (
              <div style={{ flex: dist.risk, background: '#fee2e2', color: '#b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                Risk {dist.risk}
              </div>
            )}
            {dist.grey > 0 && (
              <div style={{ flex: dist.grey, background: '#f3f4f6', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                No data {dist.grey}
              </div>
            )}
          </div>
        </div>
      )}

      {/* At-risk accounts list */}
      <div style={{
        background: 'var(--color-surface, #fff)',
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: 8,
        padding: 16,
      }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>At-risk accounts <span style={{ color: 'var(--color-text-dim)', fontWeight: 400, fontSize: 13 }}>({atRisk.length})</span></h3>
        {atRisk.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-dim)' }}>No customers currently in the Risk bucket.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)' }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Customer</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Signal</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>ARR</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Renews in</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Owner</th>
              </tr>
            </thead>
            <tbody>
              {atRisk.map((a) => (
                <tr key={a.customer_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                      background: a.chi_score < 30 ? RISK : WATCH, marginRight: 8,
                    }} />
                    <Link to={`/customers/${a.customer_id}`}>{a.company}</Link>
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--color-text-dim)', fontSize: 12 }}>
                    {a.days_since_recon !== null && a.days_since_recon !== undefined
                      ? `No day-end in ${a.days_since_recon} days`
                      : 'No day-end data'}
                    · CHI {a.chi_score}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {fmtCurrency(a.arr_total)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: a.days_to_renewal <= 30 ? RISK : a.days_to_renewal <= 90 ? WATCH : 'inherit' }}>
                    {a.nearest_renewal ? `${a.days_to_renewal}d` : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--color-text-dim)' }}>{a.owner_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
